import type { Background, Design, Widget } from './types';
import { backgroundColour, contrastRatio, isLightBackground, luminance, widgetLook } from './look';
import { WIDGETS } from './widgets';

/**
 * Readability checks for the Studio. Colours here mirror the display styles, so the checks describe what the screen
 * will really look like. Screen text is read across a room, so WCAG's large-text threshold (3:1) applies.
 */

export { luminance, contrastRatio, backgroundColour, isLightBackground } from './look';

export interface ContrastIssue {
  widgetId: string | null;
  kind: 'text' | 'edges' | 'accent' | 'image';
  severity: 'warning' | 'problem';
  /** Full sentence, starting with the widget's name. */
  message: string;
  /** The same, without the widget's name (for the widget's own settings). */
  short: string;
  /** A one-click improvement. */
  fix?: { label: string; widget?: Partial<Widget>; background?: Background };
}

export function checkContrast(d: Design): ContrastIssue[] {
  const issues: ContrastIssue[] = [];
  const bg = backgroundColour(d.background);
  const visible = d.widgets.filter((w) => !w.hidden && w.type !== 'logo');
  if (!bg) {
    const glassy = visible.filter((w) => w.style === 'glass' && w.opacity < 70);
    if (glassy.length) {
      const m = 'Glass widgets over a photo can be hard to read where the photo is bright. Check the preview, or raise the widgets’ opacity.';
      issues.push({ widgetId: null, kind: 'image', severity: 'warning', message: m, short: m });
    }
    return issues;
  }
  const light = isLightBackground(d.background);
  const where = light ? 'on this light background' : luminance(bg) > 0.12 ? 'on this background colour' : 'on this dark background';
  for (const w of visible) {
    const name = WIDGETS[w.type].label;
    const look = widgetLook(w, d.background);
    const tc = contrastRatio(look.ink, look.surface);
    // Screen text is large (it's read across a room), so the large-text standard of 3:1 applies.
    if (tc < 3 && w.type !== 'posters') {
      const short = `The text is hard to read ${w.fill ? 'on this widget colour' : where} (contrast ${tc.toFixed(1)}:1).`;
      const fix: ContrastIssue['fix'] = w.opacity < 80 ? { label: 'Make it solid', widget: { opacity: 100 } }
        : w.textColor ? { label: 'Use automatic text colour', widget: { textColor: null } }
        : w.fill ? { label: 'Use a deeper colour', widget: { fill: look.fill!.lo } }
        : { label: 'Use the 3D style', widget: { style: '3d' } };
      issues.push({ widgetId: w.id, kind: 'text', severity: tc < 2.2 ? 'problem' : 'warning', message: `${name}: ${short.charAt(0).toLowerCase()}${short.slice(1)}`, short, fix });
      continue;
    }
    const hasEdge = (w.border?.width ?? 0) > 0;
    if (!hasEdge && (w.style === 'glass' || w.style === 'flat') && contrastRatio(look.surface, bg) < 1.12 && w.type !== 'posters') {
      issues.push({
        widgetId: w.id, kind: 'edges', severity: 'warning',
        message: `${name} blends into the background, so its edges disappear.`, short: 'This widget blends into the background, so its edges disappear.',
        fix: { label: 'Use the 3D style', widget: { style: '3d' } },
      });
    }
  }
  return issues;
}

/** A short signature, so a user can dismiss today's warnings without hiding new ones. */
export const issuesKey = (issues: ContrastIssue[]) => issues.map((i) => `${i.widgetId}:${i.kind}`).sort().join('|');
